"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Input } from "@/components/ui/input"
import { Loader2, AlertCircle, ArrowRight } from "lucide-react"
import { ShaderButton } from "@/components/shader-button"
import { resolveEnsName, isLikelyEnsName, lookupEnsName, isValidEnsFormat, EnsErrorType } from "@/lib/ens"
import { ethers } from "ethers"
import { useMobile } from "@/hooks/use-mobile"
// Update the import at the top to include the new function
import { isBlacklistedAddress, hasTooManyNFTs } from "@/lib/protection"


export function WalletForm() {
  const [walletInput, setWalletInput] = useState("")
  const [resolvedAddress, setResolvedAddress] = useState("")
  const [ensName, setEnsName] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isResolving, setIsResolving] = useState(false)
  const [error, setError] = useState("")
  const [ensError, setEnsError] = useState("")
  const [ensErrorType, setEnsErrorType] = useState<EnsErrorType>(EnsErrorType.NONE)
  

  
  const router = useRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useMobile()

  // Focus the input field when the component mounts
  useEffect(() => {
    // Short timeout to ensure the input is rendered
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  // Handle ENS resolution when input changes and looks like an ENS name
  useEffect(() => {
    // Clear previous errors when input changes
    setEnsError("")
    setEnsErrorType(EnsErrorType.NONE)

    const resolveInput = async () => {
      // Clear previous resolved data
      setResolvedAddress("")
      setEnsName(null)

      // Skip empty inputs
      if (!walletInput.trim()) {
        return
      }

      // Skip if it doesn't look like an ENS name
      if (!isLikelyEnsName(walletInput)) {
        return
      }

      // Validate ENS format before attempting resolution
      if (!isValidEnsFormat(walletInput)) {
        setEnsError("Please enter a valid ENS name, ensuring it follows the correct format (e.g., example.eth).")
        setEnsErrorType(EnsErrorType.INVALID_FORMAT)
        return
      }

      setIsResolving(true)
      try {
        const result = await resolveEnsName(walletInput)

        if (result.address) {
          setResolvedAddress(result.address)
        } else if (result.error) {
          setEnsError(result.error)
          setEnsErrorType(result.errorType)
        }
      } catch (err) {
        console.error("Error resolving ENS name:", err)
        setEnsError("Error resolving ENS name. Please try again later.")
        setEnsErrorType(EnsErrorType.RESOLUTION_ERROR)
      } finally {
        setIsResolving(false)
      }
    }

    // Debounce the resolution to avoid too many requests
    const timeoutId = setTimeout(resolveInput, 500)
    return () => clearTimeout(timeoutId)
  }, [walletInput])

  // Look up ENS name for Ethereum addresses
  useEffect(() => {
    const lookupEns = async () => {
      // Skip if not a valid Ethereum address
      if (!walletInput.trim() || !ethers.isAddress(walletInput)) {
        return
      }

      setIsResolving(true)
      try {
        const name = await lookupEnsName(walletInput)
        setEnsName(name)
      } catch (err) {
        console.error("Error looking up ENS name:", err)
      } finally {
        setIsResolving(false)
      }
    }

    // Debounce the lookup to avoid too many requests
    const timeoutId = setTimeout(lookupEns, 500)
    return () => clearTimeout(timeoutId)
  }, [walletInput])

  // Update the handleSubmit function to check for blacklisted addresses
  // Find the handleSubmit function and replace it with this updated version:

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!walletInput.trim()) {
      setError("Please enter a wallet address or ENS name")
      return
    }

    // Clear error if there's input
    setError("")
    setIsLoading(true)

    try {
      // If input looks like an ENS name, resolve it
      if (isLikelyEnsName(walletInput)) {
        // Validate ENS format first
        if (!isValidEnsFormat(walletInput)) {
          setError("Please enter a valid ENS name, ensuring it follows the correct format (e.g., example.eth).")
          setIsLoading(false)
          return
        }

        const result = await resolveEnsName(walletInput)

        if (!result.address) {
          setError(result.error || "Could not resolve ENS name. Please try again.")
          setIsLoading(false)
          return
        }

        // Check if the resolved address is blacklisted
        if (isBlacklistedAddress(result.address)) {
          setError("This address cannot be processed due to its size. Please try a different address.")
          setIsLoading(false)
          return
        }

        // Check if the wallet has too many NFTs
        const tooManyNFTs = await hasTooManyNFTs(result.address)
        if (tooManyNFTs) {
          setError(
            "This wallet has too many NFTs (over 3000) and may cause performance issues. Please try a different wallet with fewer NFTs.",
          )
          setIsLoading(false)
          return
        }

        // Navigate with the resolved address - use replace to prevent back navigation to intermediate state
        router.push(`/results?wallet=${encodeURIComponent(result.address)}&ens=${encodeURIComponent(walletInput)}`, {
          scroll: false,
        })
      }
      // If input is a valid Ethereum address, use it directly
      else if (ethers.isAddress(walletInput)) {
        // Check if the wallet has too many NFTs
        const tooManyNFTs = await hasTooManyNFTs(walletInput)
        if (tooManyNFTs) {
          setError(
            "This wallet has too many NFTs (over 3000) and may cause performance issues. Please try a different wallet with fewer NFTs.",
          )
          setIsLoading(false)
          return
        }

        // If we have a resolved ENS name, include it in the URL
        const queryParams = ensName
          ? `wallet=${encodeURIComponent(walletInput)}&ens=${encodeURIComponent(ensName)}`
          : `wallet=${encodeURIComponent(walletInput)}`

        // Use replace to prevent back navigation to intermediate state
        router.push(`/results?${queryParams}`, {
          scroll: false,
        })
      }
      // Otherwise, show an error
      else {
        setError("Please enter a valid Ethereum address or ENS name.")
        setIsLoading(false)
      }
    } catch (err) {
      console.error("Form submission error:", err)
      setError("An error occurred. Please try again.")
      setIsLoading(false)
    }
  }

  // Add this effect to clear the error when the user types
  useEffect(() => {
    // Clear the form submission error when the user types
    if (walletInput.trim()) {
      setError("")
    }
  }, [walletInput])

  // Get the appropriate border color based on the ENS error type
  const getInputBorderClass = () => {
    if (ensErrorType === EnsErrorType.NONE) {
      return resolvedAddress ? "border-green-500" : ""
    }
    return "border-red-500"
  }

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="w-full">
        {/* Mobile: Inline layout with Input + Square Button */}
        {isMobile ? (
          <div className="space-y-2 w-full">
            <div className="flex gap-2 w-full">
              <div className="relative flex-1">
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Enter wallet or ENS"
                  value={walletInput}
                  onChange={(e) => setWalletInput(e.target.value)}
                  className={`h-12 text-base bg-background border-input border-gray-300 text-foreground rounded-lg pr-4 w-full font-mono ${getInputBorderClass()}`}
                  autoFocus
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                />
                {isResolving && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
              <ShaderButton
                type="submit"
                disabled={isLoading || isResolving}
                isLoading={isLoading}
                className="w-12 h-12 flex items-center justify-center"
                mode="mesh"
                palette="primary"
                aria-label="Reveal totem"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                ) : (
                  <ArrowRight className="h-5 w-5 text-white" />
                )}
              </ShaderButton>
            </div>

            {/* Show ENS validation error with appropriate styling */}
            {ensError && (
              <div className="flex items-start gap-2 text-red-500 text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{ensError}</span>
              </div>
            )}

            {/* Show resolved address or ENS name */}
            {resolvedAddress && isLikelyEnsName(walletInput) && !ensError && (
              <p className="text-sm text-green-600 flex items-center">
                <span className="inline-block w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                Resolved to: {resolvedAddress.substring(0, 6)}...{resolvedAddress.substring(resolvedAddress.length - 4)}
              </p>
            )}

            {ensName && ethers.isAddress(walletInput) && (
              <p className="text-sm text-green-600 flex items-center">
                <span className="inline-block w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                ENS Name: {ensName}
              </p>
            )}

            {error && (
              <div className="flex items-start gap-2 text-red-500 text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        ) : (
          /* Desktop: Vertical layout (unchanged) */
          <div className="space-y-4 w-full">
            <div className="space-y-2 w-full">
              <div className="relative w-full">
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Enter wallet or ENS"
                  value={walletInput}
                  onChange={(e) => setWalletInput(e.target.value)}
                  className={`h-12 text-base bg-background border-input border-gray-300 text-foreground rounded-lg pr-4 w-full font-mono ${getInputBorderClass()}`}
                  autoFocus
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                />
                {isResolving && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>

              {/* Show ENS validation error with appropriate styling */}
              {ensError && (
                <div className="flex items-start gap-2 text-red-500 text-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{ensError}</span>
                </div>
              )}

              {/* Show resolved address or ENS name */}
              {resolvedAddress && isLikelyEnsName(walletInput) && !ensError && (
                <p className="text-sm text-green-600 flex items-center">
                  <span className="inline-block w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                  Resolved to: {resolvedAddress.substring(0, 6)}...{resolvedAddress.substring(resolvedAddress.length - 4)}
                </p>
              )}

              {ensName && ethers.isAddress(walletInput) && (
                <p className="text-sm text-green-600 flex items-center">
                  <span className="inline-block w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                  ENS Name: {ensName}
                </p>
              )}

              {error && (
                <div className="flex items-start gap-2 text-red-500 text-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>

            <div className="w-full">
              <ShaderButton
                type="submit"
                disabled={isLoading || isResolving}
                isLoading={isLoading}
                fullWidth={true}
                className="h-12"
                mode="mesh"
                palette="primary"
              >
                <div className="text-primary-foreground text-base font-funnel font-medium uppercase">
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 inline animate-spin" />
                      Revealing...
                    </>
                  ) : (
                    "Reveal My Totem"
                  )}
                </div>
              </ShaderButton>
            </div>
          </div>
        )}
      </form>
    </div>
  )
}
